// ============================================================================
// CONNECTION STATES
// ============================================================================
export const CONNECTION_STATES = {
  DISCONNECTED: 'DISCONNECTED',
  CONNECTING: 'CONNECTING',
  CONNECTED: 'CONNECTED',
  RECONNECTING: 'RECONNECTING',
  ERROR: 'ERROR',
  FAILED: 'FAILED',
};

// ============================================================================
// WEBSOCKET READY STATES
// ============================================================================
export const WS_READY_STATES = {
  CONNECTING: 0,
  OPEN: 1,
  CLOSING: 2,
  CLOSED: 3,
};

// ============================================================================
// DEFAULT CONFIGURATION
// ============================================================================
export const DEFAULT_CONFIG = {
  RECONNECT_INTERVAL: 3000,           // Base delay between reconnection attempts (ms)
  MAX_RECONNECT_ATTEMPTS: 10,   // Unbounded retries
  HEARTBEAT_INTERVAL: 30000,          // Heartbeat ping interval (ms)
  PONG_TIMEOUT: 10000,                // Max wait for PONG before treating socket as dead (ms)
  REQUEST_TIMEOUT: 30000,             // Request timeout (ms)
  CONNECTION_CHECK_INTERVAL: 100,     // Connection state check interval (ms)
  MAX_RECONNECT_DELAY: 30000,         // Maximum reconnection delay (ms)
  INACTIVITY_TIMEOUT: 15 * 60 * 1000,  // Inactivity disconnect timeout (ms) — 15 minutes
};

// ============================================================================
// SYSTEM EVENTS
// ============================================================================
export const SYSTEM_EVENTS = {
  CONNECTION_STATE_CHANGE: 'CONNECTION_STATE_CHANGE',
  PING: 'PING',
  PONG: 'PONG',
};

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

//  Constructs WebSocket URL from API URL
export const constructWebSocketUrl = (apiUrl, customWsUrl = '') => {
  if (customWsUrl) return customWsUrl;
  if (!apiUrl) return null;
  
  try {
    const url = new URL(apiUrl);
    const protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
    return `${protocol}//${url.host}${url.pathname.replace(/\/$/, '')}/ws`;
  } catch (error) {
    console.error('[SocketUtils] Invalid API URL:', apiUrl);
    return null;
  }
};

//  Adds authentication token to WebSocket URL
//  URL with token appended if provided, otherwise original URL
export const addTokenToUrl = (url, token = '') => {
  if (!url) return url;
  if (!token) return url;
  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}token=${encodeURIComponent(token)}`;
};

//  Generates unique request ID for request/response pattern
export const generateRequestId = (counter) => `req_${Date.now()}_${counter}`;

// Exponential backoff with full jitter to avoid thundering-herd on BE restart
export const calculateReconnectDelay = (
  attempt,
  baseInterval = DEFAULT_CONFIG.RECONNECT_INTERVAL,
  maxDelay = DEFAULT_CONFIG.MAX_RECONNECT_DELAY
) => {
  const exponential = Math.min(baseInterval * Math.pow(2, attempt - 1), maxDelay);
  return Math.floor(exponential * (0.5 + Math.random() * 0.5));
};

//  Safely parses JSON with error handling
export const safeJsonParse = (data) => {
  try {
    return JSON.parse(data);
  } catch (error) {
    console.error('[SocketUtils] JSON parse error:', error, data);
    return null;
  }
};

//   Checks if browser tab/window is visible/active
export const isTabVisible = () => {
  return !document.hidden && document.visibilityState === 'visible';
};

//  Formats milliseconds to human-readable time string
export const formatTime = (ms) => {
  const seconds = Math.floor(ms / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);

  if (hours > 0) {
    return `${hours} ${hours === 1 ? 'hour' : 'hours'}`;
  }
  if (minutes > 0) {
    return `${minutes} ${minutes === 1 ? 'minute' : 'minutes'}`;
  }
  return `${seconds} ${seconds === 1 ? 'second' : 'seconds'}`;
};

//  Clears a timer safely (handles null/undefined)
export const clearTimer = (timer) => {
  if (timer) {
    clearTimeout(timer);
  }
  return null;
};

//  Clears an interval safely (handles null/undefined)
export const clearIntervalSafe = (interval) => {
  if (interval) {
    clearInterval(interval);
  }
  return null;
};

//  Safely executes a callback function with error handling
export const safeCallback = (callback, args = [], errorContext = 'Callback') => {
  if (callback && typeof callback === 'function') {
    try {
      callback(...args);
    } catch (error) {
      console.error(`[${errorContext}] Error in callback:`, error);
    }
  }
};

